/* eslint-disable new-cap */
/* eslint-disable camelcase */
import {
  appearanceEnum,
  noOption,
  noSolutionAvail,
  opsMissionTypeEnum,
  payloadCodeTypeEnum,
  perfResultsEnum,
  placeHolderImage_dark,
  placeHolderImage_light,
  targetPlatformEnum,
} from "../../dataStruct";
import { getHash, markDownProcessing, normalize, parseCss, round, svgDiagramDataStruct } from "../../functions";

// The perf matrix, annunciator and appendix tables are read per template, with
// params {templateId} only, so the web process-read cache answers every later
// chart of that aircraft. An app-server without the template-wide entries answers
// 400, and the per-key entry is used instead.
//
// ponytail: native reads the whole template's rows on each call instead of one
// config's. The tables are small; memoize per run if that ever shows in a trace.

/** Every performance matrix row of a template, filtered in JS to this resultCodeType, order preserved. */
export async function perfMatrixRows(
  templateId: number | string,
  perfResult: perfResultsEnum,
  classCom: any
): Promise<any[]> {
  try {
    const rows = await classCom.namedQuery("fppPerfMatrixByTemplate", { templateId });
    return rows.filter((row: any) => Number(row.resultCodeType) === Number(perfResult));
  } catch (err) {
    console.warn(
      "fppPerfMatrixByTemplate not available, falling back to fppPerfMatrixByResultCodeTypeTemplate: " + err
    );
    return classCom.namedQuery("fppPerfMatrixByResultCodeTypeTemplate", {
      resultCodeType: perfResult,
      templateId,
    });
  }
}

/** Every annunciator ("annctr") or appendix ("appdx") label row of a template, filtered in JS to this perfConfig, order preserved. */
export async function perfLabelRows(
  kind: "annctr" | "appdx",
  templateId: number | string,
  perfConfig: number | string,
  classCom: any
): Promise<any[]> {
  const templateWideName = kind === "annctr" ? "fppAnnctrByTemplate" : "fppAppdxByTemplate";
  const perKeyName = kind === "annctr" ? "fppAnnctrByPerfConfigTemplate" : "fppAppdxByPerfConfigTemplate";
  try {
    const rows = await classCom.namedQuery(templateWideName, { templateId });
    return rows.filter((row: any) => String(row.perfConfig) === String(perfConfig));
  } catch (err) {
    console.warn(templateWideName + " not available, falling back to " + perKeyName + ": " + err);
    return classCom.namedQuery(perKeyName, { perfConfig, templateId });
  }
}

export class aircraftStruct {
  fppCalcModel: modelEnum;
  registration: string;
  typeOfAcft: string;
  model: string;
  yearBuilt: string;
  make: string;
  wakeTurbCat: wTurbCatEnum;
  transponder: transpEnum;
  equipment: string;
  crusingSpeed: number;
  crusingSpeedRef: crusingSpeedRefStruct;
  acftCert: acftCertStruct;
  emergRadio: emerRadioStruct;
  surivalEqu: survEquStruct;
  colorMarkings: string;
  tacticalFuelBurn: number;
  tacticalAirspeed: number;
  tacticalStandardFuel: number;
  fuelDensity: number;
  maxUsableFuelCapacity: number;
  nbrSeatsAvail: number;
  field18: string;
  perfParameters: string;
  fplTemplates: any[];
}

export enum crusingSpeedRefStruct {
  N = 1,
  M = 2,
}
export enum modelEnum {
  rotaryWing = 1,
  fixedWing = 2,
}
export enum originMethodEnum {
  home = 1,
  lastDest = 2,
  ppos = 3,
}
export enum destinationMethodEnum {
  home = 1,
  nearest = 2,
  origin = 3,
  coordinates = 4,
}
export enum alternatenMethodEnum {
  origin = 1,
  nearest = 2,
}
export enum fuelMethodStruct {
  mtow = 1,
  minReq = 2,
  specific = 3,
  dutyOfp = 4,
}
export enum vertLevelRefStruct {
  A = 1,
  F = 2,
  VFR = 3,
}
export enum dateOfDepStruct {
  usnet = 0,
  today = 1,
  tomorrow = 2,
}
export enum timeOfDepStruct {
  usnet = 0,
  now = 1,
  fiveMin = 2,
  fivteenMin = 3,
  thirtyMin = 4,
}
export enum typeOfFlightStruct {
  G = 1,
  S = 2,
  N = 3,
  X = 4,
}
export enum transpEnum {
  S = 1,
  C = 2,
}
export enum wTurbCatEnum {
  L = 1,
  M = 2,
  H = 3,
}

export class survEquStruct {
  standard: boolean = false;
  polar: boolean = false;
  dessert: boolean = false;
  maritime: boolean = false;
  jungle: boolean = false;
  nbrOfDinghies: number = 0;
  capacityOfDinghies: number = 0;
  dinghiesHaveCover: boolean = false;
  dinghiesColor: string = "";
}
export class emerRadioStruct {
  elt: boolean = false;
  vhf: boolean = false;
  uhf: boolean = false;
}
export class acftCertStruct {
  public vfrDay: boolean = false;
  public vfrNight: boolean = false;
  public ifr: boolean = false;
}
export class CrewCargokeyValueStruct {
  crewMemberId: number;
  keyName: string;
  value: string;
}

export class payLoadStruct {
  active: boolean;
  name: string;
  priority: number;
  description: string;
  codeType: payloadCodeTypeEnum;
  weightValue: number;
  weightOrigValue: number;
  weightMin: number;
  weightMax: number;
  weightMaxUsable: number;
  weightMaxUsableOei: number;
  unitLoad: payloadUnitEnum;
  loadArmX: number;
  loadArmY: number;
  unitLoadArm: payloadLoadArmUnitEnum;
  origin: string;
  parameters: string;
  lookupObj: any;
}
export enum payloadUnitEnum {
  kg = 0,
  l,
  lbs,
  gal,
}
export enum payloadLoadArmUnitEnum {
  mm = 0,
  m,
  inches,
}

/**
 * Sentinel aircraft used when the dev "skip login" toggle bypasses the normal
 * crew/aircraft selection flow. Registration is intentionally screaming so any
 * value rendered in the UI signals "this is fake". All numeric perf fields are
 * 0 — calculations using them will produce obviously wrong results rather than
 * silently mimicking a real aircraft.
 */
const DEV_BYPASS_TAG = "DEV-BYPASS";

/**
 * Canonical dev aircraft. Always preferred over a random pick so reseed cycles
 * produce identical output and log output reads `fallback aircraft picked: OEXAM`
 * every time. Falls back to the first available row only when OEXAM is absent
 * (aircraft DB pulled before OEXAM was added).
 */
const DEV_AIRCRAFT_REGISTRATION = "OEXAM";

export const DEV_BYPASS_AIRCRAFT: aircraftStruct = (() => {
  const a = new aircraftStruct();
  a.fppCalcModel = modelEnum.rotaryWing;
  a.registration = DEV_BYPASS_TAG;
  a.typeOfAcft = DEV_BYPASS_TAG;
  a.model = DEV_BYPASS_TAG;
  a.yearBuilt = "";
  a.make = DEV_BYPASS_TAG;
  a.wakeTurbCat = wTurbCatEnum.L;
  a.transponder = transpEnum.S;
  a.equipment = "";
  a.crusingSpeed = 0;
  a.crusingSpeedRef = crusingSpeedRefStruct.N;
  a.acftCert = new acftCertStruct();
  a.emergRadio = new emerRadioStruct();
  a.surivalEqu = new survEquStruct();
  a.colorMarkings = "";
  a.tacticalFuelBurn = 0;
  a.tacticalAirspeed = 0;
  a.tacticalStandardFuel = 0;
  a.fuelDensity = 0;
  a.maxUsableFuelCapacity = 0;
  a.nbrSeatsAvail = 0;
  a.field18 = "";
  a.perfParameters = "";
  a.fplTemplates = [];
  return a;
})();

export interface DevBypassDeps {
  focMain: {
    getAircraftData(reg: string): Promise<aircraftStruct>;
  };
  classCom: {
    aircraftRegistration: string;
    namedQuery(name: string, params?: Record<string, unknown>): Promise<any[]>;
  };
}

/**
 * Dev-only: pick an aircraft for the skip-login session and nothing else.
 *
 * **This deliberately seeds NO duty, NO flightcrew row and NO flight.** It used
 * to fabricate all three ("DEV-BYPASS-DUTY", a signed `dev-bypass-sig` crew row
 * and a LOIH→LOWG flight), which is what made a bypass session unusable for
 * testing: the fake Dienst reported last-minute changes nobody made, carried no
 * loading items beyond the pilot, showed an active-signed duty as managed, and
 * mutated duty data the app is not allowed to rewrite. It also could not be
 * switched off from inside the app.
 *
 * Sign an **ad-hoc** duty in `duty-ofp-limits` instead — no roster entry
 * required — and create a flight the normal way. That path is the real product
 * behaviour, so what it exercises is worth something.
 *
 * Do not reintroduce a seed here. If a test needs fixture data, it belongs in
 * the stack's own seed, not in a code path a tester can accidentally arm.
 */
export async function resolveDevBypassAircraft(deps: DevBypassDeps): Promise<aircraftStruct> {
  const { focMain, classCom } = deps;
  const tag = "[devBypass]";
  try {
    // Pick directly from fpp_acftData (the table getAircraftData reads). The
    // legacy fpp_acftData_v1 used by focMain.getAircraftRegistrations() can be
    // out of sync with it on dev seeds, leaving us with registrations that
    // don't resolve via getAircraftData (we saw "aircraft not found: 00001").
    const acftRows = await classCom.namedQuery("fppAcftRegistrationsWithParameters");
    if (!acftRows?.length) {
      console.log(
        tag,
        "no aircraft in fpp_acftData — returning DEV_BYPASS_AIRCRAFT sentinel. Did you log in at least once so updates ran?"
      );
      return DEV_BYPASS_AIRCRAFT;
    }
    const preferred = acftRows.find((r: { registration: string }) => r.registration === DEV_AIRCRAFT_REGISTRATION);
    const reg = (preferred ?? acftRows[0]).registration;
    classCom.aircraftRegistration = reg;
    console.log(tag, "aircraft picked:", reg, "— no duty or flight seeded; sign an ad-hoc duty to test");
    const acft = await focMain.getAircraftData(reg);
    return acft ?? DEV_BYPASS_AIRCRAFT;
  } catch (err) {
    console.warn(`${tag} aircraft pick failed, falling back to sentinel`, err);
    return DEV_BYPASS_AIRCRAFT;
  }
}

/**
 * Decide which crew-seat rows survive deduplication, by row index.
 *
 * Seat rows arrive merged from up to six databases -- both names of the aircraft controller plus
 * every `ops_control_flight_perf_processor_*` that `filterDbNames` matches -- and
 * `DatabaseService.selectProcesses` flattens the results, so a row carries no marker of
 * which database it came from. `origin` is a data column, not a database marker, and does
 * not supply one.
 *
 * A seat is identified by its name, its loading arms and its `position` -- the same
 * `parseCss(parameters, "position")` that `focMain` matches a crew member's requested
 * position against when it assigns seats. That is what separates two real seats at one
 * station from two copies of one seat: copies carry the same position, a bench's 3L and
 * 3R do not.
 *
 * Both directions of getting this wrong are unsafe, which is why the position is in the
 * key rather than one of them being traded away:
 *
 * - Over-counting raises `nbrSeatsAvail`, the POB bound, and `focMain` fills the loading
 *   table up to it with standard-mass PAX rows -- mass the aircraft is not carrying.
 * - Under-counting silently drops mass. When the seat pool runs dry `focMain` warns and
 *   `continue`s, skipping the only block that writes `weightValue` and inserts the
 *   loading element, so an occupant aboard the aircraft is absent from mass and balance.
 *
 * Where copies of one seat disagree about the mass, the heaviest wins: an occupied seat
 * read as empty removes a person's mass and moment from the result.
 *
 * An arm that does not coerce to a finite number keys on its raw text instead. Coercing
 * would map every such value to `NaN`, which `JSON.stringify` writes as `null`, merging
 * two different stations into one -- and a decimal comma ("1,20") is the likely way to
 * meet that in an Austrian data set.
 *
 * Known residual: two seats sharing a name, both loading arms AND a position are
 * indistinguishable in what this query returns, and read as one. Ties are resolved by
 * keeping the first row seen, so where several rows are equal on mass the surviving row
 * -- and therefore the rest of its `parameters` -- depends on the row order, which is
 * undefined; the query carries no ORDER BY. Equal-mass rows sharing this key are copies
 * of one seat, so the choice is between equivalents.
 */
function selectCrewSeatRows(rows: any[]): Set<number> {
  const bestBySeat = new Map<string, number>();
  const mass = (i: number): number => Number(rows[i].weightValue) || 0;
  const armKey = (value: any): number | string => {
    const arm = Number(value);

    return Number.isFinite(arm) ? arm : String(value);
  };

  for (let i = 0; i < rows.length; i++) {
    const signature = JSON.stringify([
      String(rows[i].name),
      armKey(rows[i].loadArmX),
      armKey(rows[i].loadArmY),
      parseCss(String(rows[i].parameters ?? ""), "position"),
    ]);
    const incumbent = bestBySeat.get(signature);

    if (incumbent === undefined || mass(i) > mass(incumbent)) {
      bestBySeat.set(signature, i);
    }
  }

  return new Set(bestBySeat.values());
}

export class fppProvider {
  // Session-scoped cache: registration → in-flight or resolved aircraftId Promise.
  // Storing the Promise (not the resolved value) collapses concurrent first-misses to a single SQL —
  // every parallel caller awaits the same Promise instead of racing into duplicate queries.
  // Call clearAircraftIdCache() from logout/aircraft-set-switch flows if needed.
  private static aircraftIdByRegistration = new Map<string, Promise<number>>();

  static clearAircraftIdCache() {
    fppProvider.aircraftIdByRegistration.clear();
  }

  // Every loading row of one aircraft, all code types, shared while the request is in
  // flight: a page that asks for six code types at once sends one request.
  // ponytail: in-flight only, so callers that run one after another still read again.
  // Add a short-lived cache if that shows up in a measurement.
  private static loadingRowsInFlight = new Map<number, Promise<any[]>>();

  private readAircraftLoading(aircraftId: number, classCom: any): Promise<any[]> {
    let pending = fppProvider.loadingRowsInFlight.get(aircraftId);
    if (!pending) {
      pending = classCom.namedQuery("fppLoadingByAircraftId", { aircraftId });
      fppProvider.loadingRowsInFlight.set(aircraftId, pending);
      const drop = () => fppProvider.loadingRowsInFlight.delete(aircraftId);
      pending.then(drop).catch(drop);
    }
    return pending;
  }

  static getAircraftIdCacheSize(): number {
    return fppProvider.aircraftIdByRegistration.size;
  }

  constructor() {}

  async getAircraftRegistrations(classCom: any): Promise<string[]> {
    const result = await classCom.namedQuery("fppAcftRegistrationsAcrossPerf");

    const reg_lst: string[] = [];
    for (let i = 0; i < result.length; i++) {
      reg_lst.push(result[i].registration);
    }
    return reg_lst;
  }
  async getAircraftIdOfDuty(dutyId: string, classCom: any): Promise<number> {
    const result = await classCom.namedQuery("dutyAircraftRegByDutyId", { dutyId });

    return this.getAircraftIdOfRegistration(result[0].aircraftRegistration, classCom);
  }
  async getAircraftIdOfRegistration(reg: string, classCom: any): Promise<number> {
    if (reg == "OETST") {
      return 1;
    }

    const cached = fppProvider.aircraftIdByRegistration.get(reg);
    if (cached !== undefined) return cached;

    const pending = (async () => {
      const result2 = await classCom.namedQuery("fppAcftIdByRegistration", { registration: reg });
      const candidates: number[] = Array.from(
        new Set<number>(result2.map((row: { aircraftId: number }) => row.aircraftId).filter((id: number) => id != null))
      );
      if (candidates.length <= 1) return candidates[0];

      // Published databases can disagree on the ID of the same registration.
      // Resolve the ambiguity against the loading data rather than fan-out order.
      for (const aircraftId of candidates) {
        const emptyWeight = await classCom.namedQuery("fppLoadingByCodetypeAircraftId", {
          aircraftId,
          codeType: payloadCodeTypeEnum.emptyWeight,
        });
        if (emptyWeight.length > 0) return aircraftId;
      }
      throw new Error(`Aircraft ${reg}: none of the published aircraft IDs has empty-weight data`);
    })();

    fppProvider.aircraftIdByRegistration.set(reg, pending);
    try {
      const aircraftId = await pending;
      if (aircraftId === undefined) {
        // Don't pin an undefined miss — let the next call retry (e.g. data still syncing).
        fppProvider.aircraftIdByRegistration.delete(reg);
      }
      return aircraftId;
    } catch (err) {
      fppProvider.aircraftIdByRegistration.delete(reg);
      throw err;
    }
  }
  async getLoadingObjectsAircraftModel(
    registration: string,
    codetype: payloadCodeTypeEnum,
    classCom: any
  ): Promise<payLoadStruct[]> {
    try {
      const aircraftId: number = await this.getAircraftIdOfRegistration(registration, classCom);
      if (aircraftId == null) {
        throw new Error(`Aircraft ${registration}: no aircraft performance data is available`);
      }

      let result: any[];
      try {
        // Same filter the SQL used (`codeType = @codeType`); the column is INTEGER NOT NULL.
        result = (await this.readAircraftLoading(aircraftId, classCom)).filter(
          (item) => item.codeType != null && item.codeType !== "" && Number(item.codeType) === Number(codetype)
        );
      } catch (err) {
        // An app-server without fppLoadingByAircraftId refuses the read.
        console.warn("aircraft loading read failed, reading the code type alone:", err);
        result = await classCom.namedQuery("fppLoadingByCodetypeAircraftId", {
          codeType: codetype,
          aircraftId,
        });
      }
      const fcs: payLoadStruct[] = [];
      const seen = new Set<string>();
      // Seats are reconciled across database copies by selectCrewSeatRows, which needs to
      // see every row at once. Other loading classes keep the original per-row identity.
      const crewSeatRows = codetype == payloadCodeTypeEnum.crew ? selectCrewSeatRows(result) : null;

      for (let i = 0; i < result.length; i++) {
        const item = result[i];

        if (crewSeatRows !== null) {
          if (!crewSeatRows.has(i)) {
            continue;
          }
        } else {
          const key = [item.name, item.codeType, item.origin, item.loadArmX, item.loadArmY, item.priority].join("|");

          if (seen.has(key)) {
            continue;
          }

          seen.add(key);
        }

        const fc = new payLoadStruct();
        fc.active = true;
        fc.codeType = item.codeType;
        fc.description = item.description;
        fc.loadArmX = item.loadArmX;
        fc.loadArmY = item.loadArmY;
        fc.name = item.name;
        fc.origin = item.origin;
        fc.parameters = item.parameters;
        fc.priority = item.priority;
        fc.weightMax = item.weightMax;
        fc.weightMaxUsable = item.weightMaxUsable;
        fc.weightMin = item.weightMin;
        fc.weightValue = item.weightValue;

        if (item.lookupObj != null) {
          const jsonString = item.lookupObj.replace(/\xB0/g, '"').replace(/escQuoteChar/g, '"');

          if (jsonString !== "") {
            fc.lookupObj = JSON.parse(jsonString);
          }
        }

        fcs.push(fc);
      }

      if (codetype === payloadCodeTypeEnum.emptyWeight && fcs.length === 0) {
        throw new Error(`Aircraft ${registration}: empty weight and balance arms are missing from performance data`);
      }
      return fcs;
    } catch (err) {
      console.log("could not get loading objects of aircraft: xxxxx" + registration);
      throw err;
    }
  }
  async getAircraftCrew(reg: string, classCom: any): Promise<payLoadStruct[]> {
    const fcs: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(reg, payloadCodeTypeEnum.crew, classCom);

    const retFcs: payLoadStruct[] = [];
    for (let i = 0; i < fcs.length; i++) {
      try {
        if (fcs[i].weightValue > 0) {
          retFcs.push(fcs[i]);
        }
      } catch (err) {
        console.log("error getting crew: " + err);
      }
    }

    return retFcs;
  }
  async getEmptySeats(reg: string, classCom: any): Promise<payLoadStruct[]> {
    const fcs: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(reg, payloadCodeTypeEnum.crew, classCom);

    const retFcs: payLoadStruct[] = [];
    for (let i = 0; i < fcs.length; i++) {
      if (fcs[i].weightValue == 0) {
        retFcs.push(fcs[i]);
      }
    }

    return retFcs;
  }
  async getNbrOfSeats(reg: string, classCom: any): Promise<number> {
    const fcs: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(reg, payloadCodeTypeEnum.crew, classCom);

    return fcs.length;
  }
  async getMaxUsableFuelCapacityOfAircraft(reg: string, classCom: any): Promise<number> {
    const fcs: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(reg, payloadCodeTypeEnum.fuel, classCom);

    let accCapFuel: number = 0;
    for (let i = 0; i < fcs.length; i++) {
      accCapFuel += fcs[i].weightMaxUsable;
    }

    return accCapFuel;
  }

  async pushCrewAndCargoToAcft(
    dutyId: string,
    finalLoading: CrewCargokeyValueStruct[],
    classCom: any
  ): Promise<boolean> {
    return false;
  }
  async pushCargoToAcft(dutyId: number, finalLoading: CrewCargokeyValueStruct[], classCom: any): Promise<boolean> {
    return false;
  }

  async getCurrentBlockFuelOfAircraft(reg: string, classCom: any): Promise<number> {
    const fcs: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(reg, payloadCodeTypeEnum.fuel, classCom);

    let accCapFuel: number = 0;
    for (let i = 0; i < fcs.length; i++) {
      accCapFuel += fcs[i].weightValue;
    }

    return accCapFuel;
  }

  private acftDataCache: aircraftStruct;
  private lastRegAcftDataCache: string = "";
  async getAircraftData(registration: string, classCom: any): Promise<aircraftStruct> {
    // handle cache
    if (registration == this.lastRegAcftDataCache) {
      return this.acftDataCache;
    }

    try {
      // The aircraft id needs only the registration, so it is read with the data row.
      const [result] = await Promise.all([
        classCom.namedQuery("fppAcftDataByRegistration", { registration }),
        this.getAircraftIdOfRegistration(registration, classCom),
      ]);

      if (result.length == 0) {
        console.log("aircraft not found: " + registration);
        return;
      }
      const acftEntry = result[0];

      const acft = new aircraftStruct();
      acft.fppCalcModel = acftEntry.fppCalcModel;
      acft.acftCert = new acftCertStruct();
      acft.model = acftEntry.model;
      acft.make = acftEntry.make;
      acft.yearBuilt = acftEntry.yearBuilt;
      acft.acftCert.vfrDay = true;

      acft.acftCert.vfrNight = parseCss(acftEntry.parameters, "vfr-night") == "true";
      acft.acftCert.ifr = parseCss(acftEntry.parameters, "ifr") == "true";
      acft.colorMarkings = parseCss(acftEntry.parameters, "colormarkings");
      acft.field18 = parseCss(acftEntry.parameters, "field18");

      acft.perfParameters = "NIL";
      if (parseCss(acftEntry.parameters, "perf_para") !== undefined) {
        acft.perfParameters = parseCss(acftEntry.parameters, "perf_para").replaceAll("=", ":");
      }

      acft.crusingSpeedRef = crusingSpeedRefStruct[parseCss(acftEntry.parameters, "crusingSpeedRef").toUpperCase()];

      switch (acft.crusingSpeedRef) {
        case crusingSpeedRefStruct.N: {
          acft.crusingSpeed = Number(parseCss(acftEntry.parameters, "crusingSpeed")) * 0.514444;
          break;
        }

        case crusingSpeedRefStruct.M: {
          console.log("mach numbers not yet supported! [getAircraftData]");
          break;
        }
      }

      acft.emergRadio = new emerRadioStruct();
      acft.emergRadio.elt = parseCss(acftEntry.parameters, "elt") == "TRUE";
      acft.equipment = parseCss(acftEntry.parameters, "equipment");

      acft.transponder = transpEnum[parseCss(acftEntry.parameters, "transponder")];
      acft.typeOfAcft = parseCss(acftEntry.parameters, "aircraftType");

      // wake turb (hack, as forgotten in the aircraft config)
      const wtc: string = parseCss(acftEntry.parameters, "wakeTurbCat").toUpperCase();
      acft.wakeTurbCat = wTurbCatEnum[wtc];
      acft.registration = registration;

      // fuel
      acft.tacticalFuelBurn = acftEntry.tacticalFuelBurn; // per sec

      acft.tacticalAirspeed = acftEntry.tacticalAirspeed;
      acft.tacticalStandardFuel = acftEntry.tacticalStdFuel;

      acft.fuelDensity = acftEntry.fuelDensity;
      // In parallel, so both share one loading read.
      [acft.maxUsableFuelCapacity, acft.nbrSeatsAvail] = await Promise.all([
        this.getMaxUsableFuelCapacityOfAircraft(registration, classCom),
        this.getNbrOfSeats(registration, classCom),
      ]);

      acft.fplTemplates = [];

      // fill cache
      this.lastRegAcftDataCache = registration;
      this.acftDataCache = acft;

      return acft;
    } catch (err) {
      console.log("could not read aircraft: " + registration + " - " + err);
    }
    return null;
  }

  // MASS AND BALANCE functions
  // -----------------------------

  getFueltanksDynCg(fuelTanks: payLoadStruct[]): payLoadStruct[] {
    // check for lookup object -> fueltank 0
    const fuelModelFound = false;

    // get weight in tanks
    let fuelLoading: number = 0;
    for (let idx = 0; idx < fuelTanks.length; idx++) {
      fuelLoading += fuelTanks[idx].weightValue;
    }

    if (fuelModelFound == false) {
      if (fuelTanks[0].lookupObj != undefined) {
        const type = fuelTanks[0].lookupObj.model.type;
        if (type == "airbus helicopters total fuel table") {
          console.log("---fuel model found: airbus helicopters total fuel table");
          // add fuel mass
          let totalFuelMass: number = fuelLoading;

          if (totalFuelMass < 8) totalFuelMass = 10; // hack

          // find the corresponding value

          try {
            for (let idx = 1; idx < fuelTanks[0].lookupObj.model.data.length; idx++) {
              const currModelValue = fuelTanks[0].lookupObj.model.data[idx];
              const lastModelValue = fuelTanks[0].lookupObj.model.data[idx - 1];
              if (currModelValue.mass >= totalFuelMass && lastModelValue.mass <= totalFuelMass) {
                for (let fm = 0; fm < fuelTanks.length; fm++) {
                  if (lastModelValue.armX !== undefined) {
                    fuelTanks[fm].loadArmX = parseFloat(lastModelValue.armX) / 1000; // to make it M

                    // hack as cant read negative values, shitty airbus manual, solve at a later point in the FPP Data, "-" is wrong, happens only with negative values
                    if (Number.isNaN(fuelTanks[fm].loadArmX)) {
                      fuelTanks[fm].loadArmX = 0;

                      console.log("warn: #0391 airbus data corruppted negative values, set zero!");
                    }
                  }

                  // hack as cant read negative values, shitty airbus manual, solve at a later point in the FPP Data,  "-" is wrong, happens only with negative values
                  if (lastModelValue.armY !== undefined) {
                    fuelTanks[fm].loadArmY = parseFloat(lastModelValue.armY) / 1000;
                    if (Number.isNaN(fuelTanks[fm].loadArmY)) {
                      fuelTanks[fm].loadArmY = 0;

                      console.log("warn: #0391 airbus data corruppted negative values, set zero!");
                    }
                  }

                  console.log(
                    "lookup values for fuel weight: " +
                      totalFuelMass +
                      " in x: " +
                      fuelTanks[fm].loadArmX +
                      " and y: " +
                      fuelTanks[fm].loadArmY
                  );
                }

                for (const tank of fuelTanks) {
                  tank.active = true;
                }
                return fuelTanks;
              }
            }
          } catch (err) {
            console.log("warn: could not lookup dyn fuel cg");
          }
        }
      }
    }

    console.log("no cg dyn model found for fueltanks.");
    return fuelTanks;
  }

  fuelAircraft(fuelTanks: payLoadStruct[], fillingAbsolute: number): payLoadStruct[] {
    // get weight in tanks
    for (let idx = 0; idx < fuelTanks.length; idx++) {
      fuelTanks[idx].weightValue = 0;
    }

    for (let priority = 100; priority > 0; priority--) {
      for (let ft = 0; ft < fuelTanks.length; ft++) {
        if (fuelTanks[ft].priority == priority) {
          if (fillingAbsolute > 0) {
            if (fillingAbsolute > fuelTanks[ft].weightMax) {
              fuelTanks[ft].weightValue = fuelTanks[ft].weightMax;
              fillingAbsolute -= fuelTanks[ft].weightMax;
            } else {
              fuelTanks[ft].weightValue += fillingAbsolute;
              fillingAbsolute = 0;
            }
            console.log("--- fuel " + fuelTanks[ft].weightValue + " kg to " + fuelTanks[ft].name);
          }
        }
      }
    }

    fuelTanks = this.getFueltanksDynCg(fuelTanks);
    return fuelTanks;
  }

  getFuelTankData_burnFuel(fuelTanks: payLoadStruct[], burnoff: number): payLoadStruct[] {
    // fuel the aircraft with standard fuel
    // get weight in tanks
    let fuelLoading: number = 0;
    for (let idx = 0; idx < fuelTanks.length; idx++) {
      fuelLoading += fuelTanks[idx].weightValue;
      fuelTanks[idx].weightValue = 0;
    }

    fuelLoading -= burnoff;

    for (let priority = 100; priority > 0; priority--) {
      for (let ft = 0; ft < fuelTanks.length; ft++) {
        if (fuelTanks[ft].priority == priority) {
          if (fuelLoading > 0) {
            if (fuelLoading > fuelTanks[ft].weightMax) {
              fuelTanks[ft].weightValue = fuelTanks[ft].weightMax;
              fuelLoading -= fuelTanks[ft].weightMax;
            } else {
              fuelTanks[ft].weightValue += fuelLoading;
              fuelLoading = 0;
            }
          }
        }
      }
    }

    fuelTanks = this.getFueltanksDynCg(fuelTanks);

    return fuelTanks;
  }

  // markdown report of aircraft
  async getAircraftDataHtml(acftRegistration: string, classCom: any, flightId: string): Promise<string> {
    const acft = await this.getAircraftData(acftRegistration, classCom);

    // getAircraftData returns undefined for a tail that isn't in fpp_acftData
    // (bare `return;` on the empty-result branch). Every field below would
    // then throw `can't access property "registration", acft is undefined`,
    // which aborts the caller mid-render instead of degrading. Same guard the
    // three getHoistRatingLoadOf… sites in focMain.ts already carry.
    if (!acft) {
      console.warn(
        `[fpp] getAircraftDataHtml: no fleet aircraft "${acftRegistration}" in fpp_acftData — ` +
          "rendering a placeholder card instead of throwing."
      );
      // Must go through convMarkdown like every other exit of this function:
      // the callers push the result straight into /md-preview, which renders it
      // via `bypassSecurityTrustHtml`. Returning raw Markdown displayed the
      // literal `##` and `**` to the operator.
      //
      // The tail is stripped to tail characters first. showdown passes embedded
      // HTML through by default (no sanitizer is configured — `convMarkdown`
      // sets only tables/strikethrough/literalMidWordUnderscores) and the result
      // reaches `bypassSecurityTrustHtml` in `md-preview-page.component.ts`. The
      // normal exit interpolates `acft.registration`, which had to match a
      // published fleet row by bound equality; THIS branch is the one where the
      // value provably did not, and the markdown function-call dispatcher
      // (`focMain.ts`, `[]fppAcftData:` → `paraSplit[0].trim()`) hands it
      // straight out of process-report markdown. Not trusted input.
      const safeRegistration = String(acftRegistration ?? "").replace(/[^A-Za-z0-9-]/g, "") || "unknown";
      const placeholderMd = "## AIRCRAFT ON DUTY\n\nNo aircraft data for **" + safeRegistration + "**.\n";
      try {
        const mdpPlaceholder = new markDownProcessing();
        return await mdpPlaceholder.convMarkdown(
          placeholderMd,
          classCom.appearance,
          flightId,
          opsMissionTypeEnum.all,
          classCom,
          this
        );
      } catch (err) {
        // convMarkdown has its own unguarded reads (the signature block does
        // `result[0].pilotInCommand` on a query that returns [] when the active
        // duty has no efb_user flightcrew row — exactly this population). This
        // branch exists to degrade, so it must not be the thing that throws.
        console.error("[fpp] getAircraftDataHtml: placeholder render failed, returning raw markdown", err);
        return placeholderMd;
      }
    }

    const acftImageHash = getHash(acftRegistration + "-airplaneImage", 8);

    const mdText_lst: string[] = [];

    mdText_lst.push("## AIRCRAFT ON DUTY");

    mdText_lst.push("Registration **" + acft.registration + "** - model **" + acft.model + "** \n");
    mdText_lst.push("_____________");

    mdText_lst.push("- make: **" + acft.make + "**");
    mdText_lst.push("- model: **" + acft.typeOfAcft + "**");
    mdText_lst.push("- year built: **" + acft.yearBuilt + "** \n");

    mdText_lst.push("![](width:480;sql:SELECT value FROM fpp_files_v1 where codeKey=" + acftImageHash + ")");

    mdText_lst.push("|>");
    mdText_lst.push("### Equipment:");

    let certStr: string = "";
    if (acft.acftCert.vfrDay) certStr += "**VFR Day**,";
    if (acft.acftCert.vfrNight) certStr += "**VFR Night**,";
    if (acft.acftCert.ifr) certStr += "**IFR**,";

    mdText_lst.push("- certified for:" + certStr.trim().slice(0, -1));
    mdText_lst.push("- NAV Equipment: **" + acft.equipment + "**");
    mdText_lst.push("- FPL Field 18 Appendix: **" + acft.field18 + "**");
    mdText_lst.push("- PERF config: **" + acft.perfParameters + "**");
    mdText_lst.push(
      "- max usable Fuel: **" +
        acft.maxUsableFuelCapacity +
        " kg** (" +
        round(acft.maxUsableFuelCapacity / acft.fuelDensity, 0) +
        " l)"
    );

    const numberOfSeats = await this.getNbrOfSeats(acftRegistration, classCom);
    mdText_lst.push("- number of Seats (excl Flightcrew):  **" + (numberOfSeats - 2).toString() + "** ");

    mdText_lst.push(
      "- empty weight: **" +
        (await (
          await this.getLoadingObjectsAircraftModel(acftRegistration, payloadCodeTypeEnum.emptyWeight, classCom)
        )[0].weightValue) +
        " kg**"
    );

    mdText_lst.push("");
    mdText_lst.push("");
    mdText_lst.push("### Tactical Planning");
    mdText_lst.push(
      "- tactical Fuelburn: **" +
        round(acft.tacticalFuelBurn * 3600, 0) +
        " kg/h** (" +
        round((acft.tacticalFuelBurn * 3600) / acft.fuelDensity, 0) +
        " l/h)"
    );
    mdText_lst.push("- tactical TAS: **" + round(acft.tacticalAirspeed * 1.94384, 0) + " kts**  ");
    mdText_lst.push(
      "- tactical standard fuel: **" +
        acft.tacticalStandardFuel +
        "** (" +
        round(acft.tacticalStandardFuel / acft.fuelDensity, 0) +
        " l)"
    );
    mdText_lst.push("");

    const fueltanks = await this.getLoadingObjectsAircraftModel(acftRegistration, payloadCodeTypeEnum.fuel, classCom);

    mdText_lst.push("### Fueltanks");
    mdText_lst.push("| ITEM          | USABLE [kg]| ");
    mdText_lst.push("|---------------|---------|");
    for (let i = 0; i < fueltanks.length; i++) {
      mdText_lst.push("| " + fueltanks[i].name + "| " + fueltanks[i].weightMaxUsable + " | ");
    }

    const installedEquipment = await this.getLoadingObjectsAircraftModel(
      acftRegistration,
      payloadCodeTypeEnum.equipment,
      classCom
    );
    mdText_lst.push("");

    mdText_lst.push("|<");

    mdText_lst.push("### installed equipment ");
    mdText_lst.push("| ITEM          | MASS [kg] | ");
    mdText_lst.push("|---------------|---------|");
    for (let i = 0; i < installedEquipment.length; i++) {
      mdText_lst.push("| " + installedEquipment[i].name + "| " + installedEquipment[i].weightValue + " | ");
    }

    let mdString: string = "";
    for (let i = 0; i < mdText_lst.length; i++) {
      mdString += mdText_lst[i] + "\n";
    }

    const mdp = new markDownProcessing();
    const html: string = await mdp.convMarkdown(
      mdString,
      classCom.appearance,
      flightId,
      opsMissionTypeEnum.all,
      classCom,
      this
    );

    if (classCom.targetPlatform == targetPlatformEnum.server) {
      const fs = classCom.package_fs;
      fs.writeFile("out.html", html, function (err) {
        if (!err) {
          const open = classCom.package_open;
          open("out.html");
        }
      });
    }

    return html;
  }

  async getEmptyEquipped(acftReg: string, classCom: any): Promise<number> {
    try {
      const equipment: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.equipment,
        classCom
      );
      const OptEquipment: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.equipment,
        classCom
      );
      const emptyWeight: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.emptyWeight,
        classCom
      );
      let zfw: number = emptyWeight[0].weightValue;
      for (let i = 0; i < equipment.length; i++) {
        zfw += equipment[i].weightValue;
      }
      for (let i = 0; i < OptEquipment.length; i++) {
        zfw += OptEquipment[i].weightValue;
      }
      return zfw;
    } catch (err) {
      console.log("couldnt empty equipped weight! " + err);
    }
  }
  async getZeroFuelWeight(acftReg: string, classCom: any): Promise<number> {
    try {
      const equipment: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.equipment,
        classCom
      );
      const OptEquipment: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.equipment,
        classCom
      );
      const crew: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.crew,
        classCom
      );
      const cargo: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.cargo,
        classCom
      );
      const fo: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(acftReg, payloadCodeTypeEnum.fo, classCom);
      const pic: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(acftReg, payloadCodeTypeEnum.fo, classCom);
      const pax: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.pax,
        classCom
      );
      const emptyWeight: payLoadStruct[] = await this.getLoadingObjectsAircraftModel(
        acftReg,
        payloadCodeTypeEnum.emptyWeight,
        classCom
      );
      let zfw: number = emptyWeight[0].weightValue;
      for (let i = 0; i < equipment.length; i++) {
        zfw += equipment[i].weightValue;
      }
      for (let i = 0; i < OptEquipment.length; i++) {
        zfw += OptEquipment[i].weightValue;
      }

      for (let i = 0; i < crew.length; i++) {
        zfw += crew[i].weightValue;
      }
      for (let i = 0; i < cargo.length; i++) {
        zfw += cargo[i].weightValue;
      }
      for (let i = 0; i < fo.length; i++) {
        zfw += fo[i].weightValue;
      }
      for (let i = 0; i < pic.length; i++) {
        zfw += pic[i].weightValue;
      }
      for (let i = 0; i < pax.length; i++) {
        zfw += pax[i].weightValue;
      }

      return zfw;
    } catch (err) {
      console.log("couldnot calc zero fuel weight! " + err);
    }
  }

  async getPerfModelDiagram(
    acftModel: string,
    perfResult: perfResultsEnum,
    classCom: any,
    keys: string[],
    values: number[],
    desig: string[],
    avoidImageLoading: boolean = false,
    perfParameters: string = "",
    filterConditions?: string[],
    lookupOptions?: { perfConfig: number; allConditions: boolean; appearance?: appearanceEnum }
  ): Promise<svgDiagramDataStruct[]> {
    const templateId: number = Number(getHash(acftModel, 8));
    const appearance = lookupOptions?.appearance ?? classCom.appearance;
    const diagrSvgObj: svgDiagramDataStruct[] = [];

    // One matrix read answers both the availability check and the lookup.
    const perfMatrix = await perfMatrixRows(templateId, perfResult, classCom);

    if (perfMatrix.length == 0) {
      console.log(
        "no performance matrix found for model: " + acftModel + " and perfResult: " + perfResultsEnum[perfResult]
      );
      let newDiagram = this.returnInvalidDiagram(classCom);
      newDiagram[0].value = noOption;
      return newDiagram;
    }

    let parseparam = perfParameters.split(",").map((item) => item.trim());

    // loop all perf configs avail for the given perfResult codetype.
    // important to know is, that the definition must be explicit, means
    // there can only be one result on this level. The system will take
    // the first one. Uniqueness is not being checked yet.

    // the field is being loaded from the aicraft parameters field, under
    // css parameter called "perf_para", where list items comma seperated
    // are supported.

    // this section filters whether any of the parameters is matching.
    for (let i = 0; i < perfMatrix.length; i++) {
      if (lookupOptions && Number(perfMatrix[i].perfConfig) !== lookupOptions.perfConfig) continue;
      // ---> here is the main loop of this call (looping all found perf configs)
      const perfConfig = perfMatrix[i].perfConfig;
      let thisOne: boolean = true;
      if (perfMatrix[i].parameters !== "") {
        if (perfParameters !== "" || perfMatrix[i].parameters !== "") {
          thisOne = false;
          for (let p = 0; p < parseparam.length; p++) {
            if (normalize(perfMatrix[i].parameters).includes(normalize(parseparam[p]))) {
              thisOne = true;
            }
          }
        }
      }

      // "this one" means that a filter is either applied, or only one
      // definition available.
      if (thisOne) {
        // Match complete axis names. A prefix such as centerOfGravityL
        // must never send longitudinal CG into a lateral-CG chart.
        const axes: string[] = perfMatrix[i].paraPatternKey
          .split("}{")
          .map((axis: string) => axis.replace(/[{}]/g, "").trim());
        const indices = axes.map((axis) => keys.indexOf(axis));
        if (indices.some((index) => index < 0 || !Number.isFinite(values[index]))) continue;
        const finalValuesArr = indices.map((index) => values[index]);
        const limitsMin: number[] = [];
        const limitsMax: number[] = [];
        const stepsize: number[] = [];
        // here we prepare the data lookup, calculating hash according
        // to stepsize, ...
        // -----------------------------------------------------------
        const limitsMinString = perfMatrix[i].paraValuesMin.split("}{");
        const limitsMaxString = perfMatrix[i].paraValuesMax.split("}{");

        for (let g = 0; g < limitsMinString.length; g++) {
          limitsMin[g] = Number(limitsMinString[g].toString().replace("{", "").replace("}", ""));
          limitsMax[g] = Number(limitsMaxString[g].toString().replace("{", "").replace("}", ""));
        }

        // read the stepsize
        const paraValuesStepsize_min = perfMatrix[i].paraValuesStepsize.split("}{");
        for (let g = 0; g < paraValuesStepsize_min.length; g++) {
          stepsize[g] = Number(paraValuesStepsize_min[g].toString().replace("{", "").replace("}", ""));
        }

        // calc the lookupvalue using stepsize and min/max values
        for (let g = 0; g < limitsMin.length; g++) {
          for (let stepvalue = limitsMin[g]; stepvalue < limitsMax[g]; stepvalue += stepsize[g]) {
            if (stepvalue >= finalValuesArr[g] - stepsize[g] / 2) {
              finalValuesArr[g] = stepvalue;
              break;
            }
          }
        }

        // lets check whether we are within limits
        let withinLimits: boolean = false;
        for (let g = 0; g < limitsMin.length; g++) {
          if (finalValuesArr[g] >= limitsMin[g]) {
            if (finalValuesArr[g] <= limitsMax[g]) {
              withinLimits = true;
            } else {
              withinLimits = false;
            }
          } else {
            withinLimits = false;
          }
        }

        let paraKeyString: string = "";
        for (let g = 0; g < finalValuesArr.length; g++) {
          const twoDigitValue = (Math.round(finalValuesArr[g] * 1000) / 1000).toFixed(3);
          paraKeyString += "{" + twoDigitValue + "}";
        }

        console.log("---performance matrix lookup: " + perfMatrix[i].txtName + ": " + paraKeyString);
        const paraKeyHash: string = getHash(paraKeyString, 8);

        // The label reads and the data read need nothing from each other: start them together.
        const annctrRead = perfLabelRows("annctr", templateId, perfConfig, classCom);
        annctrRead.catch(() => undefined);
        const appdxRead = perfLabelRows("appdx", templateId, perfConfig, classCom);
        appdxRead.catch(() => undefined);
        const dataRead = classCom.namedQuery("fppPerfDataByKey", {
          paraKey: paraKeyHash,
          perfConfig,
          templateId,
        });
        dataRead.catch(() => undefined);

        const fpp_annctr_v1_table = await annctrRead;
        const annctr: string[] = [];
        for (let g = 0; g < fpp_annctr_v1_table.length; g++) {
          annctr.push(fpp_annctr_v1_table[g].txtAnnunciator);
        }

        const fpp_appdx_v1_table = await appdxRead;
        const appdx: string[] = [];
        for (let g = 0; g < fpp_appdx_v1_table.length; g++) {
          appdx.push(fpp_appdx_v1_table[g].txtAppendix);
        }

        // query data table
        const result = await dataRead;

        if (result.length == 0) {
          return this.returnInvalidDiagram(classCom);
        }

        // if no filter, use index -1, that lets to exit
        let idxCntr: number = result.length;
        if (filterConditions == undefined && !lookupOptions?.allConditions) {
          idxCntr = 1;
        }

        for (let g = 0; g < idxCntr; g++) {
          let useThisVersion: boolean = false;
          if (filterConditions?.length > 0) {
            if (filterConditions.includes(result[g].txtCondition)) {
              useThisVersion = true;
            }
          } else {
            // use all of them
            useThisVersion = true;
          }

          if (useThisVersion) {
            const keyDiagramFileLight: string = getHash(
              "" +
                acftModel +
                "-" +
                perfConfig +
                "-" +
                perfMatrix[i].txtName +
                "-" +
                result[g].sourceId +
                "-light-" +
                templateId,
              8
            );
            const keyDiagramFileDark: string = getHash(
              "" +
                acftModel +
                "-" +
                perfConfig +
                "-" +
                perfMatrix[i].txtName +
                "-" +
                result[g].sourceId +
                "-dark-" +
                templateId,
              8
            );

            let diagramKeyHash = "";
            if (appearance == appearanceEnum.dark) {
              diagramKeyHash = keyDiagramFileDark;
            } else {
              diagramKeyHash = keyDiagramFileLight;
            }

            const newDiagram: svgDiagramDataStruct = new svgDiagramDataStruct();
            if (appearance == appearanceEnum.dark) newDiagram.printLightKey = keyDiagramFileLight;

            if (avoidImageLoading == false) {
              const resultImage = await classCom.namedQuery("fppFilesByCodeKey", {
                codeKey: diagramKeyHash,
              });
              if (resultImage.length > 0) {
                newDiagram.image = resultImage[0].value;
                newDiagram.parameters = resultImage[0].parameters;
              }
            }

            newDiagram._isOei = perfMatrix[i].isOei;
            newDiagram._diagram_txtName = perfMatrix[i].txtName;
            newDiagram._diagram_txtDescription = perfMatrix[i].txtDescription;
            newDiagram._violation = result[g].value == noSolutionAvail;

            newDiagram._appendix = fpp_appdx_v1_table.find(
              (label) => Number(label.appendixId) === Number(result[g].appendixId)
            )?.txtAppendix;
            newDiagram._annunciator = fpp_annctr_v1_table.find(
              (label) => Number(label.annunciatorId) === Number(result[g].annunciatorId)
            )?.txtAnnunciator;

            newDiagram._txtCondition = result[g].txtCondition;
            newDiagram.value = result[g].value;

            const geometry: any = JSON.parse(result[g].sourceOverlayLines?.trim() || "[]");

            // these are the lines
            newDiagram.lines = geometry.map((line) => {
              const points = line.map((p) => {
                return [p.X, p.Y];
              });
              if (appearance == appearanceEnum.dark) {
                return {
                  points,
                  lineWidth: 3,
                  lineColor: "#ffffff",
                };
              } else {
                return {
                  points,
                  lineWidth: 3,
                  lineColor: "#000000",
                };
              }
            });

            let cnt: number = 0;
            // these are the points
            // Some source charts have lines but no point annotations.
            const points: any = JSON.parse(result[g].sourceOverlayPoints?.trim() || "[]");
            newDiagram.points = points.map((point) => {
              const position: number[] = [point.X, point.Y];

              // here is the coloring of lines
              if (appearance == appearanceEnum.dark) {
                if (newDiagram._violation == true) {
                  return {
                    position,
                    lineWidth: 1,
                    backgroundColor: "#ffff00",
                    fontColor: "#ff0000",
                    desig: desig[cnt],
                  };
                } else {
                  return {
                    position,
                    lineWidth: 1,
                    backgroundColor: "#ffffff",
                    fontColor: "#00000",
                    desig: desig[cnt],
                  };
                }
              } else {
                if (newDiagram._violation == true) {
                  return {
                    position,
                    lineWidth: 1,
                    backgroundColor: "#ffff00",
                    fontColor: "#ff0000",
                    desig: desig[cnt],
                  };
                } else {
                  return {
                    position,
                    lineWidth: 1,
                    backgroundColor: "#000000",
                    fontColor: "#ffffff",
                    desig: desig[cnt],
                  };
                }
              }
            });
            cnt += 1;
            diagrSvgObj.push(newDiagram);
          }
        }

        // returning the object here in the first loop run,
        // as by definition will only return one recult.
        // on purpose, do not move it outside the loop.
        return diagrSvgObj;
      }

      // ---> main loop ends here

      if (thisOne) {
        return this.returnInvalidDiagram(classCom);
      }
    }
    let newDiagram = this.returnInvalidDiagram(classCom);
    newDiagram[0].value = noOption;
    return newDiagram;
  }

  private returnInvalidDiagram(classCom: any): svgDiagramDataStruct[] {
    // if we geht here, filter applying did fail, respond whith a message:
    let placeholderImage: string = "";
    if (classCom.appearance == appearanceEnum.dark) {
      placeholderImage = placeHolderImage_dark;
    } else {
      placeholderImage = placeHolderImage_light;
    }

    const newDiagram: svgDiagramDataStruct = new svgDiagramDataStruct();
    newDiagram.image = placeholderImage;
    newDiagram._isPlaceholder = true;
    newDiagram.parameters = "";
    newDiagram._isOei = false;
    newDiagram._diagram_txtName = "";
    newDiagram._diagram_txtDescription = "";
    newDiagram._violation = true;
    newDiagram.parameters = "width:1500;height:1024";

    newDiagram._annunciator = "no performance config has been found or parameters filter is unsufficient!";

    return [newDiagram];
  }

  async isPerfModelAvailable(acftModel: string, perfResult: perfResultsEnum, classCom: any): Promise<boolean> {
    const templateId: number = Number(await getHash(acftModel, 8)); // <== FIXED

    const perfMatrix = await perfMatrixRows(templateId, perfResult, classCom);

    if (perfMatrix.length == 0) {
      console.log(
        "no performance matrix found for model: " + acftModel + " and perfResult: " + perfResultsEnum[perfResult]
      );
      return false;
    }

    return perfMatrix.length > 0;
  }
}
